import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import cx from 'classnames';
import { useToggleTypes } from 'hooks';
import { useSelector } from 'react-redux';
import { isAdminSelector } from 'store/selectors';
import Input from 'components/Forms/Input';
import Button from 'components/Button';
import IconButton from 'components/IconButton';
import './EditorContent.sass';

// Below this viewport width the block palette + settings panel + a 700px canvas
// no longer fit side by side (see EditorContent.sass responsive widths), so the
// canvas would start to scroll. We auto-collapse the settings (traits) panel to
// hand that space back to the canvas — the block palette (the primary build
// tool) stays visible. The user can always reopen it; a manual toggle opts out
// of the automation until the viewport crosses the threshold again.
const AUTO_COLLAPSE_TRAITS_WIDTH = 1250;
const isNarrowViewport = () =>
  typeof window !== 'undefined' &&
  window.innerWidth <= AUTO_COLLAPSE_TRAITS_WIDTH;

function EditorContent({
  editor,
  searchBlocksTerm,
  onChangeSearchBlocksTerm,
  onShow,
  blocksTab,
  customModuleGroupsCount,
  setModuleGroupTempCategory,
  setIsComponentSettingsCollapsed,
  isComponentSettingsCollapsed,
}) {
  const intl = useIntl();

  const isAdmin = useSelector(isAdminSelector);

  const [isBlocksCollapsed, setIsBlocksCollapsed] = useState(false);
  const [isTraitsCollapsed, setIsTraitsCollapsed] = useState(() =>
    isNarrowViewport(),
  );
  const [disablePointerEvents, setDisablePointerEvents] = useState(false);

  // Whether the traits panel is currently collapsed BY the responsive automation
  // (vs. by the user). Only auto-collapsed panels get auto-restored on widening.
  const traitsAutoCollapsedRef = useRef(isNarrowViewport());
  // Last-seen "is the viewport narrow" so we only act on threshold crossings,
  // never on every resize tick (which would fight a user who reopened it).
  const prevNarrowRef = useRef(isNarrowViewport());

  useEffect(() => {
    const onResize = () => {
      const narrow = isNarrowViewport();
      if (narrow === prevNarrowRef.current) return; // no crossing → do nothing
      prevNarrowRef.current = narrow;
      if (narrow) {
        setIsTraitsCollapsed((prev) => {
          if (!prev) traitsAutoCollapsedRef.current = true;
          return true;
        });
      } else if (traitsAutoCollapsedRef.current) {
        // We collapsed it on the way down → restore it on the way back up.
        traitsAutoCollapsedRef.current = false;
        setIsTraitsCollapsed(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleComponentSettings = () => {
    setIsComponentSettingsCollapsed(!isComponentSettingsCollapsed);
  };

  // Freeze pointer events during the collapse animation, with a safety timeout: if `transitionend`
  // never fires (reduced-motion, an interrupted transition, or a mismatched target) the panel would
  // otherwise stay permanently `pointer-events:none`.
  const pointerFallbackRef = useRef(null);
  const armCollapseTransition = () => {
    setDisablePointerEvents(true);
    if (pointerFallbackRef.current) clearTimeout(pointerFallbackRef.current);
    pointerFallbackRef.current = setTimeout(
      () => setDisablePointerEvents(false),
      350,
    );
  };

  const toggleBlocksCollapsing = () => {
    setIsBlocksCollapsed((prevIsBlocksCollapsed) => !prevIsBlocksCollapsed);
    armCollapseTransition();
  };

  const toggleTraitsCollapsing = () => {
    // Manual control opts out of the automation until the next threshold crossing.
    traitsAutoCollapsedRef.current = false;
    setIsTraitsCollapsed((prevIsTraitsCollapsed) => !prevIsTraitsCollapsed);
    armCollapseTransition();
  };

  const handleTransitionEnd = (e) => {
    if (e.target === e.currentTarget) {
      if (pointerFallbackRef.current) clearTimeout(pointerFallbackRef.current);
      setDisablePointerEvents(false);
      editor.refresh();
    }
  };

  return (
    <div className="editor-content">
      <div
        className={cx('editor-content__column', 'editor-content__blocks', {
          collapsed: isBlocksCollapsed,
          disabled: disablePointerEvents,
        })}
        data-testid="blocks sidebar"
        onTransitionEnd={handleTransitionEnd}>
        <div className="custom-module-preview">
          <img
            className="custom-module-preview__image"
            src="/images/image-loading.svg"
            alt=""
          />
          <hr className="custom-module-preview__divider" />
          <h1 className="custom-module-preview__title">Custom module title</h1>
          <p className="custom-module-preview__description">
            Custom module Description
          </p>
        </div>
        <Button
          aria-label="collapse blocks sidebar"
          variant="outlined"
          color="primary"
          className="editor-content__collapse-button"
          onClick={toggleBlocksCollapsing}>
          {isBlocksCollapsed ? (
            <span className="icon icon-chevron-right" />
          ) : (
            <span className="icon icon-chevron-left" />
          )}
        </Button>
        <div className="editor-content__panel-blocks-tabs" />
        <div className="editor-content__blocks-list-wrapper">
          <Input
            aria-label="search blocks"
            className="editor-content__blocks-search-input"
            placeholder={intl.formatMessage({
              id: 'editor.search-input-placeholder',
            })}
            value={searchBlocksTerm}
            onChange={onChangeSearchBlocksTerm}
            after={<span className="icon icon-search" />}
          />
          {isAdmin && blocksTab === 'modules' && (
            <div className="editor-content__blocks-list-wrapper__top-action">
              <div>
                {`${customModuleGroupsCount} ${intl.formatMessage({
                  id: 'editor.custom-modules-group-label',
                })}`}
              </div>
              <div className="editor-content__blocks-list-wrapper__creat-group-btn">
                <span>
                  {intl.formatMessage({
                    id: 'custom-modules-category.add-action-title',
                  })}
                </span>
                <IconButton
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    onShow(useToggleTypes.showCustomModulCategoryModal);
                    setModuleGroupTempCategory(null);
                  }}>
                  <span className="icon icon-add" />
                </IconButton>
              </div>
            </div>
          )}
          <div className="editor-content__blocks-list" />
        </div>
      </div>
      <div className="editor-content__column editor-content__canvas-wrapper">
        <div className="editor-content__canvas-actions">
          <div className="editor-content__panel-canvas-controls" />
          <div className="editor-content__panel-canvas-history" />
          <div className="editor-content__panel-zoom-actions">
            <div className="editor-content__panel-zoom-actions__btn" />
            <div className="editor-content__panel-zoom-actions__view">
              <span>100</span>%
            </div>
          </div>
          <div className="editor-content__panel-responsive-actions" />
        </div>
        <div id="canvas" className="editor-content__canvas" />
      </div>
      <div
        className={cx('editor-content__column', 'editor-content__traits', {
          collapsed: isTraitsCollapsed,
          disabled: disablePointerEvents,
        })}
        data-testid="traits sidebar"
        onTransitionEnd={handleTransitionEnd}>
        <Button
          aria-label="collapse traits sidebar"
          variant="outlined"
          color="primary"
          className="editor-content__collapse-button"
          onClick={toggleTraitsCollapsing}>
          {isTraitsCollapsed ? (
            <span className="icon icon-chevron-left" />
          ) : (
            <span className="icon icon-chevron-right" />
          )}
        </Button>
        <div className="editor-content__panel-traits-tabs" />
        <div className="editor-content__traits-container">
          <div
            id="component-settings"
            className={cx('component-settings', {
              open: isComponentSettingsCollapsed,
            })}>
            <div
              role="button"
              tabIndex={0}
              aria-expanded={isComponentSettingsCollapsed}
              onKeyDown={(e) => {
                // ARIA button pattern: activate on both Enter and Space.
                if (
                  e.key === 'Enter' ||
                  e.key === ' ' ||
                  e.key === 'Spacebar'
                ) {
                  e.preventDefault();

                  toggleComponentSettings();
                }
              }}
              onClick={toggleComponentSettings}
              className="component-settings__header">
              <FormattedMessage id="editor-settings.component-settings-title" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

EditorContent.propTypes = {
  editor: PropTypes.object,
  searchBlocksTerm: PropTypes.string.isRequired,
  onChangeSearchBlocksTerm: PropTypes.func.isRequired,
  onShow: PropTypes.func,
  blocksTab: PropTypes.string,
  customModuleGroupsCount: PropTypes.number,
  setModuleGroupTempCategory: PropTypes.func,
  setIsComponentSettingsCollapsed: PropTypes.func,
  isComponentSettingsCollapsed: PropTypes.bool,
};

export default EditorContent;
